// ============================================================================
// Ask AI's separation checklist card — tick one item or hand it to someone
// (item #944, the separations part; TCSD-0534 row 1, checklist items are
// cards). The server half is supabase/functions/am-copilot/separations.ts.
// Both saves are the checklist's own (hr-separation/hooks/use-separation-data.ts):
// completeItem and assignItem, with "keep this on my list" on, as the item's
// hand-on sheet defaults it.
// ============================================================================

import { assignItem, completeItem } from '../hr-separation/hooks/use-separation-data';
import { refreshReaders } from '../../shared/save-path';
import { rpcErrorMessage } from '../../shared/rpc-error';
import type { FieldSpec } from './building-actions';

export const SEPARATION_ITEM_ACTIONS = ['complete', 'assign'] as const;
export type SeparationItemAction = (typeof SEPARATION_ITEM_ACTIONS)[number];

/** A draft as the server persisted it (prepareSeparationItem). */
export interface SeparationItemDraft {
  item_id: string;
  action: SeparationItemAction;
  fields: Record<string, unknown>;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

export interface SeparationItemCardSpec {
  title: string;
  fields: FieldSpec[];
  info: Array<{ label: string; value: string }>;
  doItLabel: string;
  missing: string | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const ctxOf = (d: SeparationItemDraft) => (d.context ?? {}) as Record<string, unknown>;

/** The item as the checklist names it. */
export const separationItemOf = (d: SeparationItemDraft) => str(ctxOf(d).label) || str(d.display_name) || d.item_id;

/** What the card shows for this draft, with the person's edits `f`. */
export function separationItemCard(d: SeparationItemDraft, f: Record<string, unknown>): SeparationItemCardSpec {
  const c = ctxOf(d);
  const info = [
    { label: 'Separation of', value: str(c.person) || '—' },
    { label: 'With', value: str(c.holder) || 'Nobody yet' },
  ];
  if (d.action === 'assign') {
    return {
      title: `Hand to ${str(c.to_name) || str(f.to_eid)}`,
      fields: [{ key: 'note', label: 'Note for them (optional)', kind: 'textarea' }],
      info,
      doItLabel: 'Hand it on',
      missing: null,
    };
  }
  const needsMiles = c.needs_odometer === true;
  const fields: FieldSpec[] = [];
  if (needsMiles) fields.push({ key: 'odometer', label: 'Odometer at handover', kind: 'number', required: true });
  fields.push({ key: 'note', label: 'Note (optional)', kind: 'textarea' });
  const miles = f.odometer;
  return {
    title: 'Mark done',
    fields,
    info,
    doItLabel: 'Mark done',
    missing: needsMiles && (miles == null || str(miles).trim() === '' || Number.isNaN(Number(miles))) ? 'The odometer reading at handover, please.' : null,
  };
}

export interface SeparationItemCommitResult {
  ok: boolean;
  error?: string;
  note?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
}

/** Save one checklist draft through the checklist's own function. */
export async function commitSeparationItem(d: SeparationItemDraft, f: Record<string, unknown>): Promise<SeparationItemCommitResult> {
  const card = separationItemCard(d, f);
  if (card.missing) return { ok: false, error: card.missing };
  const note = str(f.note).trim() || null;
  try {
    if (d.action === 'assign') {
      await assignItem(d.item_id, str(f.to_eid), note, true);
    } else {
      const miles = ctxOf(d).needs_odometer === true ? Number(f.odometer) : null;
      await completeItem(d.item_id, note, miles);
    }
    refreshReaders(['separation_checklist_items']);
    return {
      ok: true,
      note: d.action === 'assign' ? `${separationItemOf(d)} — handed on.` : `${separationItemOf(d)} — done.`,
      target_row_id: d.item_id,
      committed_payload: d.action === 'assign' ? { to_eid: str(f.to_eid) } : { done: true },
    };
  } catch (e) {
    return { ok: false, error: rpcErrorMessage(e, 'That did not save.') };
  }
}
